import { ITEM_IMAGE_FILE_TYPE_NAME } from '../catalog/itemImage';
import { ASSET_IMAGE_FILE_TYPE_NAME } from '../assets/assetImage';
import type { FileTypeRecordType } from '../modals/uploadModal';
import { ARTICLE_PICTURE_FILE_TYPE_NAME } from '../knowledge/pictures';
import { EMAIL_PICTURE_FILE_TYPE_NAME } from '../modals/emailBody';
import { ORGANIZATION_LOGO_FILE_TYPE_NAME } from '../records/organizationLogo';
import { IMPORT_REPORT_FILE_TYPE_NAME } from '../import/importRun';
import { COMPANY_LOGO_FILE_TYPE_NAME, LOCATION_PHOTO_FILE_TYPE_NAME, PERSON_PHOTO_FILE_TYPE_NAME } from '../records/recordPicture';
import { PERFORMANCE_REVIEW_FILE_TYPE_NAME } from '../hr/reviews';
import { PROPOSAL_REPORT_FILE_TYPE_NAME } from '../deals/askClaude';
import { CHECK_STOCK_FILE_TYPE_NAME } from '../print/checkStock';

/** The certificate's seeded type (piece 10; decision 0274), filed on the Site that holds it. */
export const TAX_EXEMPT_CERTIFICATE_FILE_TYPE_NAME = 'Tax Exempt Certificate';

/** The vendor's Form W-9 (piece 24; decision 0667), filed on the Company from its Vendor Tile. */
export const W9_FILE_TYPE_NAME = 'W-9';

/** 🔒 Decision 7850 (T3170): a customer's application for credit terms, a standard form filed on its Company. */
export const CREDIT_APPLICATION_FILE_TYPE_NAME = 'Credit Application';

/** 🔒 Decision 7850 (T3170): a trade or bank reference a credit application names, a standard form filed on its Company. */
export const CREDIT_REFERENCE_FILE_TYPE_NAME = 'Credit Reference';

/** 🔒 Decision 9680 (T3368): the employee's signed Employment Agreement, the employment form whose answers write their personal NDA. */
export const EMPLOYMENT_AGREEMENT_FILE_TYPE_NAME = 'Employment Agreement';

/** 🔒 Decision 9680 (T3368): the offer a new hire signed, an employment form asking the date signed alone. */
export const OFFER_LETTER_FILE_TYPE_NAME = 'Offer Letter';

/** 🔒 Decision 9680 (T3368): the employee's signed handbook acknowledgement, an employment form asking the date signed alone. */
export const HANDBOOK_ACKNOWLEDGEMENT_FILE_TYPE_NAME = 'Handbook Acknowledgement';

/**
 * 🔒 A receipt (piece 24; T741, decision 1475): uploaded tagged to no record, so
 * it waits in the Documents Inbox until an expense line names it. The receipt
 * pane offers it, and the Expenses page's drop zone files every receipt under it.
 */
export const RECEIPT_FILE_TYPE_NAME = 'Receipt';

/**
 * 🔒 Decision 2910: the type every visit photo upload is filed under, on the Work Order alone. 🔒 Decision 13311 (T3835): it
 * joined the seeded types at the picture standard; its Settings rows say what it is for, keyed to this constant, so the
 * name can change without the line.
 */
export const VISIT_NOTE_FILE_TYPE_NAME = 'Visit Note';

/** Piece 32, decision 0798: every inbound mail attachment, filed on the Message it arrived on. */
export const MAIL_ATTACHMENT_FILE_TYPE_NAME = 'Mail Attachment';

/**
 * 🔒 Decision 6380 and the owner's answer of 2026-10-21 (row T2967): every seeded
 * picture type starts at this standard, a long edge of 2400 pixels and JPEG quality
 * 75, Site Photo's own. Article Picture joined them in decision 10512 (T3573), and Visit Note in decision 13311 (T3835). An administrator edits it per type in Settings afterwards.
 */
export const PICTURE_STANDARD = { longEdgePx: 2400, jpegQuality: 75 } as const;

/**
 * The records a seeded type names: the upload list's own, and the Work Order, which the list cannot name and the Visit
 * Note type has always been created on (decision 13311).
 */
export type SeededAppliesTo = FileTypeRecordType | 'WorkOrder';

export interface SeededFileType {
  name: string;
  classification: 'Public' | 'Accounting' | 'Commercial' | 'General' | 'Engineering' | 'Legal' | 'Purchasing' | 'HR';
  customerMayDownload: boolean;
  /**
   * 🔒 The records the type belongs to (decision 0667): a record's upload offers
   * the types naming it and the types naming nothing (decision 0299). 🔨 Empty
   * only where the record it is filed on is a document the list cannot name.
   */
  appliesTo: readonly SeededAppliesTo[];
  /** 🔒 A type the application writes or assigns itself, which no generic upload window offers (decision 2420). */
  isSystem?: boolean;
  /** 🔒 Decision 6380: the image standard a starter type is created with, the long edge and the JPEG quality; the type compresses on upload. */
  standard?: { readonly longEdgePx: number; readonly jpegQuality: number };
  /** 🔒 Decision 9680: an employment form, which the Person page's Employment Documents offers and whose upload asks the date signed. */
  isEmploymentForm?: boolean;
}

/**
 * 🔒 Every file type the install seeds for one kind of record, and the record it
 * names (decision 0667). The seed, the migration that named the types already
 * standing, and each service that creates its type where a database predates it
 * all read this one list, so none of them can offer a type somewhere the others
 * would not.
 */
export const SEEDED_FILE_TYPES: readonly SeededFileType[] = [
  { name: ITEM_IMAGE_FILE_TYPE_NAME, classification: 'Public', customerMayDownload: true, appliesTo: ['Item'], isSystem: true, standard: PICTURE_STANDARD },
  // Decision 1291: an asset node's picture, on the Item image's terms, filed on the node alone.
  { name: ASSET_IMAGE_FILE_TYPE_NAME, classification: 'Public', customerMayDownload: true, appliesTo: ['AssetNode'], isSystem: true, standard: PICTURE_STANDARD },
  { name: PERSON_PHOTO_FILE_TYPE_NAME, classification: 'Public', customerMayDownload: true, appliesTo: ['PersonRecord'], isSystem: true, standard: PICTURE_STANDARD },
  { name: COMPANY_LOGO_FILE_TYPE_NAME, classification: 'Public', customerMayDownload: true, appliesTo: ['CompanyRecord'], isSystem: true, standard: PICTURE_STANDARD },
  { name: LOCATION_PHOTO_FILE_TYPE_NAME, classification: 'Public', customerMayDownload: true, appliesTo: ['LocationRecord'], isSystem: true, standard: PICTURE_STANDARD },
  // The organisation logo is tagged to no record; the Organization is a Company, so it names that and nothing else.
  { name: ORGANIZATION_LOGO_FILE_TYPE_NAME, classification: 'Public', customerMayDownload: true, appliesTo: ['CompanyRecord'], isSystem: true, standard: PICTURE_STANDARD },
  // 🔒 Decision 7850: a standard form names its records, the certificate the Site that holds it and the Company it exempts; decision 9600 adds the Purchase Order its one-time form is kept on.
  { name: TAX_EXEMPT_CERTIFICATE_FILE_TYPE_NAME, classification: 'Accounting', customerMayDownload: true, appliesTo: ['SiteRecord', 'CompanyRecord', 'PurchaseOrder'], isSystem: true },
  { name: W9_FILE_TYPE_NAME, classification: 'Accounting', customerMayDownload: false, appliesTo: ['CompanyRecord'], isSystem: true },
  // Decision 10512 (T3573): the import and the editor's picture tool reach the one picture pipeline, so the type starts at the shared standard; the owner may turn it off on the File Types page.
  // Decision 0800: a picture in an article's words is tagged to no record; an article documents a part, so the type names the Item and nothing else.
  { name: ARTICLE_PICTURE_FILE_TYPE_NAME, classification: 'Public', customerMayDownload: false, appliesTo: ['Item'], isSystem: true, standard: PICTURE_STANDARD },
  // Decision 0965: a picture in an email template's words is tagged to no record and goes out to customers inside the mail; the Organization is a Company, as the logo's type names.
  { name: EMAIL_PICTURE_FILE_TYPE_NAME, classification: 'Public', customerMayDownload: false, appliesTo: ['CompanyRecord'], isSystem: true, standard: PICTURE_STANDARD },
  // Piece 32, decision 0798: read through the mailbox's own gate, so its class is the general one and nobody downloads it from outside.
  { name: MAIL_ATTACHMENT_FILE_TYPE_NAME, classification: 'General', customerMayDownload: false, appliesTo: ['Message'], isSystem: true },
  // Piece 31, decision 0921: an import run's diff, filed on the Organization's own Company; it names customers, vendors and accounts, so it is the books' class and nobody downloads it from outside.
  { name: IMPORT_REPORT_FILE_TYPE_NAME, classification: 'Accounting', customerMayDownload: false, appliesTo: ['CompanyRecord'], isSystem: true },
  // T741, decision 1475: a receipt is filed on no record, so the type names none, and the books' class keeps it from a customer.
  { name: RECEIPT_FILE_TYPE_NAME, classification: 'Accounting', customerMayDownload: false, appliesTo: [] },
  // Decision 3185: a signed performance review, filed on the job from the Employee page's Reviews section. The job is no record the list names, so the type names none, and the HR class keeps it to HR.
  { name: PERFORMANCE_REVIEW_FILE_TYPE_NAME, classification: 'HR', customerMayDownload: false, appliesTo: [], isSystem: true },
  // Decision 6003 (T2780 batch 2): the proposal report Make Report writes on the Deal, the Deal's own class, and never a customer's download.
  { name: PROPOSAL_REPORT_FILE_TYPE_NAME, classification: 'Commercial', customerMayDownload: false, appliesTo: ['Deal'], isSystem: true },
  // Decision 7630 (T3126): a scan of the organization's own blank check, named by a bank account and tagged to no record; it shows the bank's details, so it is the books' class and nobody's download, and no standard re-draws it.
  { name: CHECK_STOCK_FILE_TYPE_NAME, classification: 'Accounting', customerMayDownload: false, appliesTo: [], isSystem: true },
  // 🔒 Decision 13311 (T3835, the owner's P2a): a visit's photos, filed on the Work Order alone, never a customer's download; new photos take the picture standard.
  { name: VISIT_NOTE_FILE_TYPE_NAME, classification: 'General', customerMayDownload: false, appliesTo: ['WorkOrder'], isSystem: true, standard: PICTURE_STANDARD },
];

/**
 * 🔒 A service business's starter list of document types (decision 2423; T1595),
 * seeded once on an install so a record's upload offers more than a receipt. Each
 * is a generic type a person picks, never the system's own, and an administrator
 * edits or retires it in Settings. 🔨 The seed never writes one over a type whose
 * name already stands in any case, so a list an organization typed itself stays
 * its own. A type naming no record is offered on every record (decision 0299).
 */
export const STARTER_FILE_TYPES: readonly SeededFileType[] = [
  { name: 'Contract', classification: 'Legal', customerMayDownload: true, appliesTo: [] },
  { name: 'Drawing', classification: 'Engineering', customerMayDownload: true, appliesTo: [] },
  { name: 'Specification', classification: 'Engineering', customerMayDownload: true, appliesTo: [] },
  // 🔒 Decision 6380: the owner's measured standard of 2026-10-20, every catalog number readable at about half a megabyte.
  { name: 'Site Photo', classification: 'Public', customerMayDownload: true, appliesTo: [], standard: PICTURE_STANDARD },
  { name: 'Permit', classification: 'Legal', customerMayDownload: true, appliesTo: [] },
  { name: 'Certificate of Insurance', classification: 'Legal', customerMayDownload: false, appliesTo: ['CompanyRecord'] },
  { name: 'Warranty', classification: 'Commercial', customerMayDownload: true, appliesTo: [] },
  { name: 'Vendor Quote', classification: 'Purchasing', customerMayDownload: false, appliesTo: ['CompanyRecord', 'Deal', 'Item'] },
  { name: 'Signed Work Order', classification: 'Commercial', customerMayDownload: true, appliesTo: [] },
  { name: 'Inspection Report', classification: 'Engineering', customerMayDownload: true, appliesTo: [] },
  // 🔒 Decision 4804: the types a Zoho bill's and purchase order's files come in under, the books' and the buyer's classes, never a customer's download.
  { name: 'Vendor Bill', classification: 'Accounting', customerMayDownload: false, appliesTo: ['Bill'] },
  { name: 'Purchase Order', classification: 'Purchasing', customerMayDownload: false, appliesTo: ['PurchaseOrder'] },
  // 🔒 Decision 7850 (T3170): the two credit forms a person files on a customer, standard forms naming the Company alone, so no deal's photo drop zone offers them; the books' class, never a customer's download.
  { name: CREDIT_APPLICATION_FILE_TYPE_NAME, classification: 'Accounting', customerMayDownload: false, appliesTo: ['CompanyRecord'] },
  { name: CREDIT_REFERENCE_FILE_TYPE_NAME, classification: 'Accounting', customerMayDownload: false, appliesTo: ['CompanyRecord'] },
  // 🔒 Decision 9680 (T3368): the three employment forms, filed on the employee's Person under the HR class, which keeps them to HR and encrypts them; never a customer's download.
  { name: EMPLOYMENT_AGREEMENT_FILE_TYPE_NAME, classification: 'HR', customerMayDownload: false, appliesTo: ['PersonRecord'], isEmploymentForm: true },
  { name: OFFER_LETTER_FILE_TYPE_NAME, classification: 'HR', customerMayDownload: false, appliesTo: ['PersonRecord'], isEmploymentForm: true },
  { name: HANDBOOK_ACKNOWLEDGEMENT_FILE_TYPE_NAME, classification: 'HR', customerMayDownload: false, appliesTo: ['PersonRecord'], isEmploymentForm: true },
];

/**
 * 🔒 Every name a type the application writes or assigns itself has carried
 * (decisions 2420, 2423): the Title Case name it carries now, and the sentence-case
 * name it was seeded under before, which the migration renamed.
 */
export const SYSTEM_FILE_TYPE_RENAMES: readonly (readonly [string, string])[] = [
  ['Item image', 'Item Image'],
  ['Asset image', 'Asset Image'],
  ['Person photo', 'Person Photo'],
  ['Company logo', 'Company Logo'],
  ['Location photo', 'Location Photo'],
  ['Organization logo', 'Organization Logo'],
  ['Tax exempt certificate', 'Tax Exempt Certificate'],
  ['Article picture', 'Article Picture'],
  ['Email picture', 'Email Picture'],
  ['Mail attachment', 'Mail Attachment'],
  ['Import report', 'Import Report'],
  ['Customer purchase order', 'Customer Purchase Order'],
  ['Signed copy', 'Signed Copy'],
  ['Service report', 'Service Report'],
  ['Checklist answer', 'Checklist Answer'],
];

/** The list a seeded type is created with; a name the list does not hold applies everywhere. */
export function seededAppliesTo(name: string): SeededAppliesTo[] {
  return [...(SEEDED_FILE_TYPES.find((row) => row.name === name)?.appliesTo ?? [])];
}

/**
 * 🔒 The compression columns a seeded type is created with (decision 6380; T2993): the standard its row names, so a
 * service that creates its type where a database predates the seed creates it at the same standard the seed does.
 * A type with no standard keeps the pipeline off.
 */
export function seededStandardData(name: string): { compressOnUpload: boolean; longEdgePx: number | null; jpegQuality: number | null } {
  const standard = SEEDED_FILE_TYPES.find((row) => row.name === name)?.standard;
  return { compressOnUpload: standard !== undefined, longEdgePx: standard?.longEdgePx ?? null, jpegQuality: standard?.jpegQuality ?? null };
}

/**
 * 🔒 A document the application asks for by name (decision 2420): its type is
 * set by the place that asks, and its slug names every stored version of it
 * (decision 2421). `versionsPerRecord` says the upload itself finds the standing
 * document, the newest untrashed file of the type on the record, and makes the
 * new file its next version with no question asked. Where it is false the
 * asking service names the standing document, as the certificate's does.
 */
export interface SystemAssignedFileType {
  name: string;
  slug: string;
  versionsPerRecord: boolean;
}

export const SYSTEM_ASSIGNED_FILE_TYPES: readonly SystemAssignedFileType[] = [
  { name: W9_FILE_TYPE_NAME, slug: 'w9', versionsPerRecord: true },
  { name: TAX_EXEMPT_CERTIFICATE_FILE_TYPE_NAME, slug: 'tax-exempt-certificate', versionsPerRecord: false },
  // Decision 6003: a second Make Report is the Deal's report's next version, found by the upload itself.
  { name: PROPOSAL_REPORT_FILE_TYPE_NAME, slug: 'proposal-report', versionsPerRecord: true },
];

/** The system-assigned entry a type's name names, or null for every other type. */
export function systemAssignedFileType(name: string): SystemAssignedFileType | null {
  return SYSTEM_ASSIGNED_FILE_TYPES.find((row) => row.name === name) ?? null;
}
